/**
 * Momentum and impulse.
 *
 *   p = m v                  linear momentum (a vector: v carries a sign along the line)
 *   F Δt = m (v − u)         impulse–momentum theorem: the impulse of the net force equals
 *                            the change in momentum, J = Δp
 *
 * OpenStax University Physics Vol. 1, §9.1 (linear momentum) and §9.2 (impulse and
 * collisions). Velocities are signed along a line, so a velocity opposite to the positive
 * direction is negative and the momentum comes out negative.
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  FORCE,
  MASS,
  MOMENTUM,
  SPEED,
  TIME,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

export type MomentumKey = "p" | "m" | "v";

export const LINEAR_MOMENTUM: RelationDef<MomentumKey> = {
  keys: ["p", "m", "v"],
  symbols: { p: "p", m: "m", v: "v" },
  families: { p: MOMENTUM, m: MASS, v: SPEED },
  equation: "p = m × v",
  solvedFor: "p",
  rearranged: { p: "p = m × v", m: "m = p ÷ v", v: "v = p ÷ m" },
  solve(unknown, { p, m, v }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown === "p") {
      return m * v;
    }
    if (unknown === "v") {
      return p / m;
    }
    if (v === 0) {
      return fail("speed-zero");
    }
    const mass = p / v;
    return mass > 0 ? mass : fail("mass-sign");
  },
};

export type ImpulseKey = "F" | "t" | "m" | "u" | "v";

export const IMPULSE_MOMENTUM: RelationDef<ImpulseKey> = {
  keys: ["F", "t", "m", "u", "v"],
  symbols: { F: "F", t: "Δt", m: "m", u: "u", v: "v" },
  families: { F: FORCE, t: TIME, m: MASS, u: SPEED, v: SPEED },
  equation: "F × Δt = m × (v − u)",
  solvedFor: "F",
  rearranged: {
    F: "F = m × (v − u) ÷ Δt",
    t: "Δt = m × (v − u) ÷ F",
    m: "m = F × Δt ÷ (v − u)",
    u: "u = v − F × Δt ÷ m",
    v: "v = u + F × Δt ÷ m",
  },
  solve(unknown, { F, t, m, u, v }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown !== "t" && t < 0) {
      return fail("time-negative");
    }
    switch (unknown) {
      case "F":
        return t > 0 ? (m * (v - u)) / t : fail("time-zero");
      case "t": {
        if (F === 0) {
          return fail("force-zero");
        }
        const time = (m * (v - u)) / F;
        return time > 0 ? time : fail("direction-mismatch");
      }
      case "m": {
        if (v === u) {
          return fail("no-velocity-change");
        }
        const mass = (F * t) / (v - u);
        return mass > 0 ? mass : fail("direction-mismatch");
      }
      case "u":
        return v - (F * t) / m;
      default:
        return u + (F * t) / m;
    }
  },
};

export type CollisionType = "elastic" | "inelastic" | "partial";

export interface CollisionInput {
  m1: number;
  u1: number;
  m2: number;
  u2: number;
  type: CollisionType;
  /** Coefficient of restitution, used only for the partial type: 0 ≤ e ≤ 1. */
  restitution: number;
}

export type CollisionError = "mass1" | "mass2" | "restitution" | "no-collision";

export interface CollisionResult {
  v1: number;
  v2: number;
  /** The coefficient of restitution used: 1 elastic, 0 perfectly inelastic. */
  restitution: number;
  momentumBefore: number;
  momentumAfter: number;
  keBefore: number;
  keAfter: number;
  keLost: number;
  /** keLost ÷ keBefore (0 when nothing was moving). */
  fractionLost: number;
}

/**
 * A head-on collision in one dimension between object 1, on the left, and object 2, on
 * the right. Momentum is conserved whatever the type; the coefficient of restitution e —
 * the speed of separation over the speed of approach — fixes the rest:
 *
 *   v₁ = (m₁u₁ + m₂u₂ + m₂ e (u₂ − u₁)) / (m₁ + m₂)
 *   v₂ = (m₁u₁ + m₂u₂ + m₁ e (u₁ − u₂)) / (m₁ + m₂)
 *
 * e = 1 is an elastic collision (kinetic energy conserved) and e = 0 a perfectly
 * inelastic one (the objects move off together).
 */
export function solveCollision(
  input: CollisionInput
): CollisionResult | { error: CollisionError } {
  const { m1, u1, m2, u2, type } = input;
  if (!(m1 > 0)) {
    return { error: "mass1" };
  }
  if (!(m2 > 0)) {
    return { error: "mass2" };
  }
  let e = input.restitution;
  if (type === "elastic") {
    e = 1;
  } else if (type === "inelastic") {
    e = 0;
  } else if (!(e >= 0 && e <= 1)) {
    return { error: "restitution" };
  }
  // Object 1 starts on the left, so they meet only if it is the faster to the right.
  if (!(u1 > u2)) {
    return { error: "no-collision" };
  }
  const total = m1 + m2;
  const momentum = m1 * u1 + m2 * u2;
  const v1 = (momentum + m2 * e * (u2 - u1)) / total;
  const v2 = (momentum + m1 * e * (u1 - u2)) / total;
  const keBefore = 0.5 * m1 * u1 * u1 + 0.5 * m2 * u2 * u2;
  const keAfter = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;
  const keLost = keBefore - keAfter;
  return {
    v1,
    v2,
    restitution: e,
    momentumBefore: momentum,
    momentumAfter: m1 * v1 + m2 * v2,
    keBefore,
    keAfter,
    keLost: Math.abs(keLost) < 1e-12 * keBefore ? 0 : keLost,
    fractionLost: keBefore > 0 ? Math.max(0, keLost) / keBefore : 0,
  };
}
